import { useState, useCallback, useEffect, useRef } from 'react';
import { generatePresignedUrls } from '../actions/files/generatePresignedUrls';
import { getCurrentUserId } from '../utils/getCurrentUserId';
import { checkPdfPassword, validatePdfPassword } from '../utils/pdfPasswordDetector';
import { verifyMultiplePdfs, validatePdfPassword as validatePdfPasswordBackend } from '../actions/files/verifyPdf';
import { getDocumentChatStatus } from '../actions/document-chat';
import { useAuth } from '../contexts/AuthContext';

export interface DualUploadFormState {
  // Primary upload area
  primaryFiles: File[];
  primaryError: string;
  
  // Secondary upload area  
  secondaryFiles: File[];
  secondaryError: string;
  
  // General form state
  processing: boolean;
  showLoader: boolean;
  refresh: boolean;
  useThinking: boolean;
  enableDocumentChat: boolean;
  jobId: string;
  userId: string;
  
  // Document chat state (premium feature)
  documentChatStatus: {
    chatEnabled: boolean;
    isPremiumUser: boolean;
  } | null;
  documentChatOpen: boolean;
  hasDocumentChatAccess: boolean;

  // File size information
  fileSizeInfo: {
    currentSize: number;
    currentSizeMB: string;
    maxSizeMB: number;
    maxSizeBytes: number;
    isOverLimit: boolean;
    progressPercentage: number;
  };

  // Password handling state
  filePasswords: Record<string, string>;
  currentFileNeedingPassword: File | null;
  showPasswordModal: boolean;
  isCheckingPasswords: boolean;
}

export interface DualUploadFormActions {
  // Primary upload actions
  setPrimaryFiles: (files: File[]) => void;
  setPrimaryError: (error: string) => void;
  removePrimaryFile: (file: File) => void;
  createPrimaryDropHandler: (isApproved: boolean) => (files: File[]) => void;
  
  // Secondary upload actions
  setSecondaryFiles: (files: File[]) => void;
  setSecondaryError: (error: string) => void;
  removeSecondaryFile: (file: File) => void;
  createSecondaryDropHandler: (isApproved: boolean) => (files: File[]) => void;
  
  // General actions
  setProcessing: (processing: boolean) => void;
  setShowLoader: (show: boolean) => void;
  setUseThinking: (thinking: boolean) => void;
  setEnableDocumentChat: (enabled: boolean) => void;
  setJobId: (jobId: string) => void;
  handleRefreshComplete: () => void;
  
  // Document chat actions
  checkDocumentChatStatus: (jobId: string) => Promise<void>;
  handleOpenDocumentChat: () => void;
  handleCloseDocumentChat: () => void;

  // Upload handling
  createApiUploadHandler: (
    apiCall: (primaryKeys: string[], secondaryKeys: string[], filePasswords?: Record<string, string>) => Promise<any>,
    onSuccess?: () => void
  ) => (files: File[], setProgressCallback: (progress: number) => void) => Promise<string>;
  
  // Form validation and submission
  handleSubmit: (e: React.FormEvent, customValidation?: () => boolean) => void;
  
  // Reset functions
  clearAllFiles: () => void;
  handleModalClose: () => void;

  // Password handling actions
  handlePasswordSubmit: (password: string) => void | Promise<void>;
  handlePasswordSkip: () => void;
  handlePasswordCancel: () => void;
}

interface DualUploadFormConfig {
  primaryMinFiles?: number;
  secondaryMinFiles?: number;
  maxTotalSizeMB?: number;
  primaryErrorMessage?: string;
  secondaryErrorMessage?: string;
}

export const useDualUploadForm = (config: DualUploadFormConfig = {}): DualUploadFormState & DualUploadFormActions => {
  const {
    primaryMinFiles = 1,
    secondaryMinFiles = 0,
    maxTotalSizeMB = 60,
    primaryErrorMessage = "Please upload at least one file in the primary area.",
    secondaryErrorMessage = "Please upload at least one file in the secondary area."
  } = config;

  const { setGateModalOpen } = useAuth();

  // Document chat access is now determined by backend (documentChatStatus?.isPremiumUser)

  // File state
  const [primaryFiles, setPrimaryFiles] = useState<File[]>([]);
  const [secondaryFiles, setSecondaryFiles] = useState<File[]>([]);
  const [primaryError, setPrimaryError] = useState<string>("");
  const [secondaryError, setSecondaryError] = useState<string>("");

  // General state
  const [processing, setProcessing] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [useThinking, setUseThinking] = useState<boolean>(false);
  const [enableDocumentChat, setEnableDocumentChat] = useState<boolean>(false);
  const [jobId, setJobId] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  // Guard to prevent re-running API create call after client (4xx) errors
  const [clientErrorLock, setClientErrorLock] = useState<boolean>(false);

  // Document chat state (premium feature)
  const [documentChatStatus, setDocumentChatStatus] = useState<{
    chatEnabled: boolean;
    isPremiumUser: boolean;
  } | null>(null);
  const [documentChatOpen, setDocumentChatOpen] = useState(false);

  // Password handling state
  const [filePasswords, setFilePasswords] = useState<Record<string, string>>({});
  const [currentFileNeedingPassword, setCurrentFileNeedingPassword] = useState<File | null>(null);
  const [pendingPasswordFiles, setPendingPasswordFiles] = useState<{ file: File; targetArea: 'primary' | 'secondary' }[]>([]);
  const [isCheckingPasswords, setIsCheckingPasswords] = useState<boolean>(false);

  // Ref to capture passwords at submission time
  const filePasswordsRef = useRef<Record<string, string>>({});

  // Refs for backend-detected password prompts (upload-time encryption detection)
  const backendPasswordResolveRef = useRef<((password: string) => void) | null>(null);
  const backendPasswordRejectRef = useRef<((error: Error) => void) | null>(null);
  const isBackendPasswordRequestRef = useRef<boolean>(false);
  const backendPasswordS3KeyRef = useRef<string>('');

  // File size calculations
  const BYTES_PER_MB = 1024 * 1024;
  const MAX_SINGLE_FILE_SIZE_MB = 30;
  const MAX_SINGLE_FILE_SIZE_BYTES = MAX_SINGLE_FILE_SIZE_MB * BYTES_PER_MB;
  const MAX_SIZE_BYTES = maxTotalSizeMB * BYTES_PER_MB;

  const calculateTotalSize = (): number => {
    const allFiles = [...primaryFiles, ...secondaryFiles];
    return allFiles.reduce((total, file) => total + file.size, 0);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0.0";
    const mb = bytes / BYTES_PER_MB;
    return mb.toFixed(1);
  };

  const currentSize = calculateTotalSize();
  const currentSizeMB = formatFileSize(currentSize);
  const isOverLimit = currentSize > MAX_SIZE_BYTES;
  const progressPercentage = Math.min((currentSize / MAX_SIZE_BYTES) * 100, 100);

  const fileSizeInfo = {
    currentSize,
    currentSizeMB,
    maxSizeMB: maxTotalSizeMB,
    maxSizeBytes: MAX_SIZE_BYTES,
    isOverLimit,
    progressPercentage
  };

  // Get current user ID when component mounts
  useEffect(() => {
    const getUserId = async () => {
      const currentUserId = await getCurrentUserId();
      setUserId(currentUserId || "");
    };
    getUserId();
  }, []);

  // Check document chat status for a job
  const checkDocumentChatStatus = useCallback(async (jobIdToCheck: string) => {
    if (!jobIdToCheck) return;
    try {
      const status = await getDocumentChatStatus(jobIdToCheck);
      setDocumentChatStatus({
        chatEnabled: status.chatEnabled,
        isPremiumUser: status.isPremiumUser
      });
    } catch (error) {
      console.warn('Failed to get document chat status:', error);
      setDocumentChatStatus(null);
    }
  }, []);

  // Handle opening chat
  const handleOpenDocumentChat = useCallback(() => {
    if (jobId) {
      setDocumentChatOpen(true);
    }
  }, [jobId]);

  // Handle closing chat
  const handleCloseDocumentChat = useCallback(() => {
    setDocumentChatOpen(false);
  }, []);

  // Process next pending password file
  const processNextPendingPasswordFile = useCallback(() => {
    if (pendingPasswordFiles.length === 0) {
      setCurrentFileNeedingPassword(null);
      return;
    }

    const [next, ...remaining] = pendingPasswordFiles;
    setCurrentFileNeedingPassword(next.file);
    setPendingPasswordFiles(remaining);
  }, [pendingPasswordFiles]);

  // Handle password submission
  const handlePasswordSubmit = useCallback((password: string) => {
    if (!currentFileNeedingPassword) return;

    // Store the password
    setFilePasswords(prev => ({
      ...prev,
      [currentFileNeedingPassword.name]: password
    }));

    // Find which area this file was for and add it
    const pendingItem = pendingPasswordFiles.find(p => p.file.name === currentFileNeedingPassword.name);
    const currentItem = { file: currentFileNeedingPassword, targetArea: pendingItem?.targetArea || 'primary' };
    
    // Find the original pending entry to determine target area
    // We need to check if this was the current file or find its entry
    // Since current file has already been removed from pending, we need to track this differently
    // Let's use a state to track which area the current file belongs to
    
    // For now, we'll add to primary - but we should track this properly
    // Actually, let's check if the file matches any in our pending state before it was removed
    
    // Add file to appropriate area
    if (currentItem.targetArea === 'secondary') {
      setSecondaryFiles(prev => [...prev, currentFileNeedingPassword]);
    } else {
      setPrimaryFiles(prev => [...prev, currentFileNeedingPassword]);
    }

    // Move to next pending file
    if (pendingPasswordFiles.length > 0) {
      const [next, ...remaining] = pendingPasswordFiles;
      setCurrentFileNeedingPassword(next.file);
      setPendingPasswordFiles(remaining);
    } else {
      setCurrentFileNeedingPassword(null);
    }
  }, [currentFileNeedingPassword, pendingPasswordFiles]);

  // Handle password skip
  const handlePasswordSkip = useCallback(() => {
    // If this is a backend password request, reject the promise
    if (isBackendPasswordRequestRef.current && backendPasswordRejectRef.current) {
      backendPasswordRejectRef.current(new Error('User skipped password entry'));
      isBackendPasswordRequestRef.current = false;
      backendPasswordS3KeyRef.current = '';
      backendPasswordResolveRef.current = null;
      backendPasswordRejectRef.current = null;
      setCurrentFileNeedingPassword(null);
      return;
    }

    // Skip current file (don't add it)
    if (pendingPasswordFiles.length > 0) {
      const [next, ...remaining] = pendingPasswordFiles;
      setCurrentFileNeedingPassword(next.file);
      setPendingPasswordFiles(remaining);
    } else {
      setCurrentFileNeedingPassword(null);
    }
  }, [pendingPasswordFiles]);

  // Handle password cancel
  const handlePasswordCancel = useCallback(() => {
    // If this is a backend password request, reject the promise
    if (isBackendPasswordRequestRef.current && backendPasswordRejectRef.current) {
      backendPasswordRejectRef.current(new Error('User cancelled password entry'));
      isBackendPasswordRequestRef.current = false;
      backendPasswordS3KeyRef.current = '';
      backendPasswordResolveRef.current = null;
      backendPasswordRejectRef.current = null;
    }

    setCurrentFileNeedingPassword(null);
    setPendingPasswordFiles([]);
  }, []);

  // Primary file handlers
  const removePrimaryFile = useCallback((fileToRemove: File) => {
    setPrimaryFiles(prev => prev.filter(file => file.name !== fileToRemove.name));
    // Also remove password if exists
    setFilePasswords(prev => {
      const newPasswords = { ...prev };
      delete newPasswords[fileToRemove.name];
      return newPasswords;
    });
  }, []);

  // Create drop handler for files with password detection
  const createDropHandlerWithPasswordCheck = useCallback((
    targetArea: 'primary' | 'secondary',
    isApproved: boolean,
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    setError: React.Dispatch<React.SetStateAction<string>>
  ) => {
    return async (acceptedFiles: File[]) => {
      if (!isApproved) {
        setGateModalOpen(true);
        return;
      }

      if (acceptedFiles.length === 0) return;

      // Check individual file size limits
      const oversizedFiles = acceptedFiles.filter(f => f.size > MAX_SINGLE_FILE_SIZE_BYTES);
      if (oversizedFiles.length > 0) {
        const fileList = oversizedFiles
          .map(f => `${f.name} (${(f.size / BYTES_PER_MB).toFixed(1)} MB)`)
          .join(', ');
        setError(
          `File${oversizedFiles.length > 1 ? 's' : ''} too large: ${fileList}. ` +
          `Maximum allowed per file is ${MAX_SINGLE_FILE_SIZE_MB} MB.`
        );
        return;
      }

      setIsCheckingPasswords(true);
      setError("");

      // Only deduplicate within the target zone (same file can exist in both zones for comparison)
      const targetFiles = targetArea === 'primary' ? primaryFiles : secondaryFiles;
      const existingNames = new Set(targetFiles.map(f => f.name));
      const uniqueFiles = acceptedFiles.filter(f => !existingNames.has(f.name));

      if (uniqueFiles.length === 0) {
        setIsCheckingPasswords(false);
        return;
      }

      // Separate PDFs from non-PDFs
      const pdfFiles = uniqueFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
      const nonPdfFiles = uniqueFiles.filter(f => !f.name.toLowerCase().endsWith('.pdf'));

      // Add non-PDF files immediately
      if (nonPdfFiles.length > 0) {
        setFiles(prev => [...prev, ...nonPdfFiles]);
      }

      // Check PDFs for password protection
      const passwordProtectedFiles: { file: File; targetArea: 'primary' | 'secondary' }[] = [];
      const unprotectedPdfs: File[] = [];

      for (const file of pdfFiles) {
        const result = await checkPdfPassword(file);
        if (result.needsPassword) {
          passwordProtectedFiles.push({ file, targetArea });
        } else {
          unprotectedPdfs.push(file);
        }
      }

      // Add unprotected PDFs immediately
      if (unprotectedPdfs.length > 0) {
        setFiles(prev => [...prev, ...unprotectedPdfs]);
      }

      // Queue password-protected files
      if (passwordProtectedFiles.length > 0) {
        const [first, ...rest] = passwordProtectedFiles;
        setCurrentFileNeedingPassword(first.file);
        setPendingPasswordFiles(prev => [...prev, ...rest]);
      }

      setIsCheckingPasswords(false);
    };
  }, [primaryFiles, secondaryFiles, MAX_SINGLE_FILE_SIZE_BYTES, BYTES_PER_MB, setGateModalOpen]);

  // Track which area the current file needing password belongs to
  const [currentFileTargetArea, setCurrentFileTargetArea] = useState<'primary' | 'secondary'>('primary');

  // Updated create drop handlers
  const createPrimaryDropHandler = useCallback((isApproved: boolean) => {
    return async (acceptedFiles: File[]) => {
      if (!isApproved) {
        setGateModalOpen(true);
        return;
      }

      if (acceptedFiles.length === 0) return;

      // Check individual file size limits
      const oversizedFiles = acceptedFiles.filter(f => f.size > MAX_SINGLE_FILE_SIZE_BYTES);
      if (oversizedFiles.length > 0) {
        const fileList = oversizedFiles
          .map(f => `${f.name} (${(f.size / BYTES_PER_MB).toFixed(1)} MB)`)
          .join(', ');
        setPrimaryError(
          `File${oversizedFiles.length > 1 ? 's' : ''} too large: ${fileList}. ` +
          `Maximum allowed per file is ${MAX_SINGLE_FILE_SIZE_MB} MB.`
        );
        return;
      }

      setIsCheckingPasswords(true);
      setPrimaryError("");

      // Only deduplicate within the primary zone (same file can exist in both zones for comparison)
      const existingPrimaryNames = new Set(primaryFiles.map(f => f.name));
      const uniqueFiles = acceptedFiles.filter(f => !existingPrimaryNames.has(f.name));

      if (uniqueFiles.length === 0) {
        setIsCheckingPasswords(false);
        return;
      }

      // Separate PDFs from non-PDFs
      const pdfFiles = uniqueFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
      const nonPdfFiles = uniqueFiles.filter(f => !f.name.toLowerCase().endsWith('.pdf'));

      // Add non-PDF files immediately
      if (nonPdfFiles.length > 0) {
        setPrimaryFiles(prev => [...prev, ...nonPdfFiles]);
      }

      // Check PDFs for password protection
      const passwordProtectedFiles: { file: File; targetArea: 'primary' | 'secondary' }[] = [];
      const unprotectedPdfs: File[] = [];

      for (const file of pdfFiles) {
        const result = await checkPdfPassword(file);
        if (result.needsPassword) {
          passwordProtectedFiles.push({ file, targetArea: 'primary' });
        } else {
          unprotectedPdfs.push(file);
        }
      }

      // Add unprotected PDFs immediately
      if (unprotectedPdfs.length > 0) {
        setPrimaryFiles(prev => [...prev, ...unprotectedPdfs]);
      }

      // Queue password-protected files
      if (passwordProtectedFiles.length > 0) {
        const [first, ...rest] = passwordProtectedFiles;
        setCurrentFileNeedingPassword(first.file);
        setCurrentFileTargetArea('primary');
        setPendingPasswordFiles(prev => [...prev, ...rest]);
      }

      setIsCheckingPasswords(false);
    };
  }, [primaryFiles, setGateModalOpen]);

  // Secondary file handlers
  const removeSecondaryFile = useCallback((fileToRemove: File) => {
    setSecondaryFiles(prev => prev.filter(file => file.name !== fileToRemove.name));
    // Also remove password if exists
    setFilePasswords(prev => {
      const newPasswords = { ...prev };
      delete newPasswords[fileToRemove.name];
      return newPasswords;
    });
  }, []);

  const createSecondaryDropHandler = useCallback((isApproved: boolean) => {
    return async (acceptedFiles: File[]) => {
      if (!isApproved) {
        setGateModalOpen(true);
        return;
      }

      if (acceptedFiles.length === 0) return;

      // Check individual file size limits
      const oversizedFiles = acceptedFiles.filter(f => f.size > MAX_SINGLE_FILE_SIZE_BYTES);
      if (oversizedFiles.length > 0) {
        const fileList = oversizedFiles
          .map(f => `${f.name} (${(f.size / BYTES_PER_MB).toFixed(1)} MB)`)
          .join(', ');
        setSecondaryError(
          `File${oversizedFiles.length > 1 ? 's' : ''} too large: ${fileList}. ` +
          `Maximum allowed per file is ${MAX_SINGLE_FILE_SIZE_MB} MB.`
        );
        return;
      }

      setIsCheckingPasswords(true);
      setSecondaryError("");

      // Only deduplicate within the secondary zone (same file can exist in both zones for comparison)
      const existingSecondaryNames = new Set(secondaryFiles.map(f => f.name));
      const uniqueFiles = acceptedFiles.filter(f => !existingSecondaryNames.has(f.name));

      if (uniqueFiles.length === 0) {
        setIsCheckingPasswords(false);
        return;
      }

      // Separate PDFs from non-PDFs
      const pdfFiles = uniqueFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
      const nonPdfFiles = uniqueFiles.filter(f => !f.name.toLowerCase().endsWith('.pdf'));

      // Add non-PDF files immediately
      if (nonPdfFiles.length > 0) {
        setSecondaryFiles(prev => [...prev, ...nonPdfFiles]);
      }

      // Check PDFs for password protection
      const passwordProtectedFiles: { file: File; targetArea: 'primary' | 'secondary' }[] = [];
      const unprotectedPdfs: File[] = [];

      for (const file of pdfFiles) {
        const result = await checkPdfPassword(file);
        if (result.needsPassword) {
          passwordProtectedFiles.push({ file, targetArea: 'secondary' });
        } else {
          unprotectedPdfs.push(file);
        }
      }

      // Add unprotected PDFs immediately
      if (unprotectedPdfs.length > 0) {
        setSecondaryFiles(prev => [...prev, ...unprotectedPdfs]);
      }

      // Queue password-protected files
      if (passwordProtectedFiles.length > 0) {
        const [first, ...rest] = passwordProtectedFiles;
        setCurrentFileNeedingPassword(first.file);
        setCurrentFileTargetArea('secondary');
        setPendingPasswordFiles(prev => [...prev, ...rest]);
      }

      setIsCheckingPasswords(false);
    };
  }, [secondaryFiles, setGateModalOpen]);

  // Updated password submit to use target area tracking
  // Now validates the password before accepting (modal delegates validation here)
  const handlePasswordSubmitWithArea = useCallback(async (password: string) => {
    if (!currentFileNeedingPassword) return;

    // Check if this is a backend password request (upload-time detection)
    if (isBackendPasswordRequestRef.current && backendPasswordS3KeyRef.current) {
      // Validate with backend API since client-side pdf.js couldn't detect the encryption
      const validation = await validatePdfPasswordBackend(backendPasswordS3KeyRef.current, password);

      if (validation.valid) {
        // Resolve the promise to unblock the upload handler
        if (backendPasswordResolveRef.current) {
          backendPasswordResolveRef.current(password);
        }
        // Reset backend password state
        isBackendPasswordRequestRef.current = false;
        backendPasswordS3KeyRef.current = '';
        backendPasswordResolveRef.current = null;
        backendPasswordRejectRef.current = null;
        setCurrentFileNeedingPassword(null);
      } else {
        throw new Error(validation.error || 'Incorrect password');
      }
      return;
    }

    // Client-side detected encryption - validate with pdf.js
    const result = await validatePdfPassword(currentFileNeedingPassword, password);
    if (!result.valid) {
      throw new Error(result.error || 'Incorrect password');
    }

    // Store the password
    setFilePasswords(prev => ({
      ...prev,
      [currentFileNeedingPassword.name]: password
    }));

    // Add file to appropriate area based on tracked target
    if (currentFileTargetArea === 'secondary') {
      setSecondaryFiles(prev => [...prev, currentFileNeedingPassword]);
    } else {
      setPrimaryFiles(prev => [...prev, currentFileNeedingPassword]);
    }

    // Move to next pending file
    if (pendingPasswordFiles.length > 0) {
      const [next, ...remaining] = pendingPasswordFiles;
      setCurrentFileNeedingPassword(next.file);
      setCurrentFileTargetArea(next.targetArea);
      setPendingPasswordFiles(remaining);
    } else {
      setCurrentFileNeedingPassword(null);
    }
  }, [currentFileNeedingPassword, currentFileTargetArea, pendingPasswordFiles]);

  // Refresh handling
  const handleRefreshComplete = useCallback(() => {
    setRefresh(false);
  }, []);

  // Clear functions
  const clearAllFiles = useCallback(() => {
    setPrimaryFiles([]);
    setSecondaryFiles([]);
    setPrimaryError("");
    setSecondaryError("");
    setFilePasswords({});
    setEnableDocumentChat(false);
    setCurrentFileNeedingPassword(null);
    setPendingPasswordFiles([]);
    setIsCheckingPasswords(false);
    filePasswordsRef.current = {};
    // Reset backend password request state
    isBackendPasswordRequestRef.current = false;
    backendPasswordS3KeyRef.current = '';
    backendPasswordResolveRef.current = null;
    backendPasswordRejectRef.current = null;
  }, []);

  // Modal close handler
  const handleModalClose = useCallback(() => {
    setShowLoader(false);
    setProcessing(false);
    clearAllFiles();
    setJobId("");
    setDocumentChatStatus(null);
    setRefresh(true);
  }, [clearAllFiles]);

  // Upload handler
  const createApiUploadHandler = useCallback((
    apiCall: (primaryKeys: string[], secondaryKeys: string[], filePasswords?: Record<string, string>) => Promise<any>,
    onSuccess?: () => void
  ) => {
    return async (files: File[], setProgressCallback: (progress: number) => void): Promise<string> => {
      try {
        // Capture current passwords at submission time
        const currentPasswords = filePasswordsRef.current;

        // If we previously encountered a client-side error, block further attempts
        if (clientErrorLock) {
          throw new Error("Previous request failed due to a client error. Please adjust inputs or try again later.");
        }

        if (files.length === 0) {
          const response = await apiCall([], [], undefined);
          const extractedJobId = response.data?.jobId || response.jobId;

          if (!extractedJobId || extractedJobId.trim() === "") {
            throw new Error("Invalid server response: Missing job identifier.");
          }

          setJobId(extractedJobId);
          onSuccess?.();
          return response.data?.message || response.message || "Process started successfully.";
        }

        // Step 1: Request presigned URLs for each file
        const presignedUrls = await generatePresignedUrls(
          files.map(file => ({
            filename: file.name,
            contentType: file.type,
            size: file.size,
          }))
        );
        let currentProgress = 0;

        // Step 2: Upload each file using its presigned URL
        await Promise.all(
          presignedUrls.map(async (urlObj, index) => {
            const file = files[index];
            const res = await fetch(urlObj.url, {
              method: "PUT",
              headers: { "Content-Type": file.type },
              body: file,
            });
            if (!res.ok) {
              throw new Error(`Upload failed for ${file.name}`);
            }
            currentProgress += 100 / files.length;
            setProgressCallback(currentProgress);
          })
        );

        // Step 3: Separate keys into primary and secondary arrays
        const primaryKeys = presignedUrls.slice(0, primaryFiles.length).map(obj => obj.key);
        const secondaryKeys = presignedUrls.slice(primaryFiles.length).map(obj => obj.key);

        // Step 3.5a: Re-map passwords from filename → S3 key
        // Frontend stores passwords keyed by filename, but backend expects S3 keys
        const s3KeyPasswords: Record<string, string> = {};
        presignedUrls.forEach((urlObj) => {
          const password = currentPasswords[urlObj.filename];
          if (password) {
            s3KeyPasswords[urlObj.key] = password;
          }
        });

        // Step 3.5b: Backend PDF verification (catches encryption frontend might miss)
        // If backend detects encryption, pause and show password modal to user
        const pdfUrls = presignedUrls.filter(
          urlObj => urlObj.filename.toLowerCase().endsWith('.pdf')
        );

        if (pdfUrls.length > 0) {
          console.log(`[useDualUploadForm] Backend verification for ${pdfUrls.length} PDF(s)...`);

          // Collect encrypted PDFs that need passwords
          const encryptedFiles: Array<{ urlObj: typeof pdfUrls[0]; file: File }> = [];

          // Split into files that already have passwords vs. those that need checking
          const urlsWithPassword = pdfUrls.filter(urlObj => !!s3KeyPasswords[urlObj.key]);
          const urlsWithoutPassword = pdfUrls.filter(urlObj => !s3KeyPasswords[urlObj.key]);

          // Validate pre-supplied passwords individually
          await Promise.all(
            urlsWithPassword.map(async (urlObj) => {
              const existingPassword = s3KeyPasswords[urlObj.key];
              const validation = await validatePdfPasswordBackend(urlObj.key, existingPassword);
              if (!validation.valid) {
                console.warn(`[useDualUploadForm] Backend rejected password for ${urlObj.filename}`);
                const matchedFile = files.find(f => f.name === urlObj.filename);
                if (matchedFile) {
                  encryptedFiles.push({ urlObj, file: matchedFile });
                }
              }
            })
          );

          // Verify all password-free PDFs in a single batch request
          if (urlsWithoutPassword.length > 0) {
            const batchResults = await verifyMultiplePdfs(
              urlsWithoutPassword.map(u => u.key)
            );
            for (const urlObj of urlsWithoutPassword) {
              const result = batchResults.get(urlObj.key);
              if (result?.needsPassword) {
                console.log(`[useDualUploadForm] Backend detected encryption for ${urlObj.filename}`);
                const matchedFile = files.find(f => f.name === urlObj.filename);
                if (matchedFile) {
                  encryptedFiles.push({ urlObj, file: matchedFile });
                }
              }
            }
          }

          // If backend found encrypted PDFs, prompt user for passwords one by one
          if (encryptedFiles.length > 0) {
            console.log(`[useDualUploadForm] Prompting user for ${encryptedFiles.length} password(s) via modal...`);

            for (const { urlObj, file } of encryptedFiles) {
              try {
                // Create a promise that resolves when the user enters the password in the modal
                const password = await new Promise<string>((resolve, reject) => {
                  backendPasswordResolveRef.current = resolve;
                  backendPasswordRejectRef.current = reject;
                  isBackendPasswordRequestRef.current = true;
                  backendPasswordS3KeyRef.current = urlObj.key;
                  // Triggers the PdfPasswordModal to open
                  setCurrentFileNeedingPassword(file);
                });

                // Password was validated and accepted - store it
                s3KeyPasswords[urlObj.key] = password;
                console.log(`[useDualUploadForm] Password accepted for ${urlObj.filename}`);
              } catch (err: any) {
                // User skipped or cancelled - abort upload
                console.warn(`[useDualUploadForm] Password not provided for ${urlObj.filename}:`, err.message);
                throw new Error(
                  `🔒 Password required for: ${urlObj.filename}\n\n` +
                  `This file is encrypted and requires a password to process. ` +
                  `Please close this dialog and try again, providing the correct password when prompted.`
                );
              }
            }
          }
        }

        // Step 4: Call the API with separated keys and passwords
        const hasPasswords = Object.keys(s3KeyPasswords).length > 0;
        const response = await apiCall(
          primaryKeys, 
          secondaryKeys, 
          hasPasswords ? s3KeyPasswords : undefined
        );

        // Step 5: Validate response and extract jobId
        const extractedJobId = response.data?.jobId || response.jobId;
        
        if (!extractedJobId || extractedJobId.trim() === "") {
          console.error("❌ API Response validation failed - missing jobId:", response);
          throw new Error("Invalid server response: Missing job identifier. Please close this dialog and resubmit your request, or contact support if the issue persists.");
        }

        setJobId(extractedJobId);
        
        // Clear files and call success handler
        // NOTE: We don't clear files here anymore, we clear them in handleModalClose
        // clearAllFiles();
        onSuccess?.();

        return response.data?.message || response.message || "Process started successfully.";
      } catch (error: any) {
        // Detect 4xx client errors and enable lock to prevent further automatic/manual re-submits
        const status: number | undefined = error?.response?.status;
        if (typeof status === 'number' && status >= 400 && status < 500) {
          setClientErrorLock(true);
          // Close modal and reset processing to prevent any loops
          setProcessing(false);
          setShowLoader(false);
          // Surface a concise error to the user
          const message = error?.response?.data?.message || 'Request failed. Please check your inputs and try again.';
          alert(message);
        }
        throw error;
      }
    };
  }, [primaryFiles.length, clientErrorLock]);

  // Form validation
  const validateForm = useCallback((): boolean => {
    let isValid = true;

    if (primaryFiles.length < primaryMinFiles) {
      setPrimaryError(primaryErrorMessage);
      isValid = false;
    } else {
      setPrimaryError("");
    }

    if (secondaryMinFiles > 0 && secondaryFiles.length < secondaryMinFiles) {
      setSecondaryError(secondaryErrorMessage);
      isValid = false;
    } else {
      setSecondaryError("");
    }

    return isValid;
  }, [primaryFiles.length, secondaryFiles.length, primaryMinFiles, secondaryMinFiles, primaryErrorMessage, secondaryErrorMessage]);

  // Form submission
  const handleSubmit = useCallback((e: React.FormEvent, customValidation?: () => boolean) => {
    e.preventDefault();
    
    // Block submission if a client error lock is active
    if (clientErrorLock) {
      alert("Cannot submit: previous request failed due to a client error. Please refresh the page or adjust your inputs and try again.");
      return;
    }

    const isFormValid = customValidation ? customValidation() : validateForm();
    
    if (!isFormValid) {
      return;
    }

    // Capture passwords at submission time
    filePasswordsRef.current = { ...filePasswords };
    
    setProcessing(true);
    setShowLoader(true);
  }, [validateForm, clientErrorLock, filePasswords]);

  // Computed property for document chat access
  const hasDocumentChatAccess = documentChatStatus?.isPremiumUser || false;

  return {
    // State
    primaryFiles,
    primaryError,
    secondaryFiles,
    secondaryError,
    processing,
    showLoader,
    refresh,
    useThinking,
    enableDocumentChat,
    jobId,
    userId,
    documentChatStatus,
    documentChatOpen,
    hasDocumentChatAccess, // Computed: isPremiumUser from backend
    fileSizeInfo,
    filePasswords,
    currentFileNeedingPassword,
    showPasswordModal: currentFileNeedingPassword !== null,
    isCheckingPasswords,

    // Actions
    setPrimaryFiles,
    setPrimaryError,
    removePrimaryFile,
    createPrimaryDropHandler,
    setSecondaryFiles,
    setSecondaryError,
    removeSecondaryFile,
    createSecondaryDropHandler,
    setProcessing,
    setShowLoader,
    setUseThinking,
    setEnableDocumentChat,
    setJobId,
    handleRefreshComplete,
    checkDocumentChatStatus,
    handleOpenDocumentChat,
    handleCloseDocumentChat,
    createApiUploadHandler,
    handleSubmit,
    clearAllFiles,
    handleModalClose,
    handlePasswordSubmit: handlePasswordSubmitWithArea,
    handlePasswordSkip,
    handlePasswordCancel
  };
};
